import {Component, OnDestroy, OnInit} from '@angular/core';
import {BasketsFacade} from '@kion/data-access/data-access-baskets';
import {BehaviorSubject, interval, Observable} from 'rxjs';
import {Basket} from '@kion/data-models';
import {debounce, map, take} from 'rxjs/operators';
import {Sum} from '../../../../../lr-ui/src/lib/elements/basket-summary/basket-summary.component';
import {CorrelationObserverService} from '@kion/ngrx-helper';
import {ToastService} from '@kion/ui';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {SubSink} from 'subsink';
import {CmsConfig} from '../../config/cms.config';
import {TranslateService} from '@ngx-translate/core';
import {BaseSitesFacade} from "@kion/data-access/data-access-base-sites";

@Component({
  selector: 'kion-cms-basket',
  templateUrl: './basket.component.html',
  styleUrls: ['./basket.component.scss']
})
export class BasketComponent implements OnInit, OnDestroy {

  basket$: Observable<Basket>;
  pricing$: Observable<Sum>;
  isMultiVendor$: Observable<boolean> = this.baseSiteFacade.all$.pipe(
    map(sites => sites.find(site => site.uid === this.cmsConfig.baseSite)),
    map(site => site.multiVendor)
  );


  form: FormGroup;
  voucherError$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  subSink: SubSink;

  checkoutLink: string;

  constructor(
    private basketsFacade: BasketsFacade,
    private observer: CorrelationObserverService,
    private toastService: ToastService,
    private cmsConfig: CmsConfig,
    private translateService: TranslateService,
    formBuilder: FormBuilder,
    private baseSiteFacade: BaseSitesFacade
  ) {
    this.form = formBuilder.group(
      {
        voucher: new FormControl()
      }
    );
    this.subSink = new SubSink();
    this.checkoutLink = '/' + this.cmsConfig.routes?.checkoutBaseRoute;
  }

  ngOnInit(): void {
    this.basketsFacade.createOrFindBasket();
    this.basket$ = this.basketsFacade.all$.pipe(
      map(
        (baskets: Basket[]) => baskets.find(
          (basket: Basket) => basket.id === window.localStorage.getItem('basketId')
        )
      )
    );
    this.pricing$ = this.basket$.pipe(
      map(
        (basket: Basket) => {
          if (basket) {
            return {
              subTotal: basket.subTotal.formattedValue,
              total: basket.totalPriceWithTax.formattedValue,
              delivery: basket.deliveryCost.formattedValue,
              vat: basket.totalTax.formattedValue,
              discount: basket.totalDiscounts.value !== 0 ? basket.totalDiscounts.formattedValue : null,
              subTotalWithDiscounts: basket.totalDiscounts.value !== 0 ? basket.subTotalWithDiscounts.formattedValue : null
            }
          }
        }
      )
    );
    this.subSink.sink = this.basket$.subscribe(
      basket => {
        if (basket) {
          if (basket.appliedVouchers[0]) {
            this.form.get('voucher').patchValue(basket.appliedVouchers[0].code);
          }
        }
      }
    );
    this.subSink.sink = this.form.get('voucher').valueChanges
      .pipe(debounce(() => interval(500)))
      .subscribe(
        voucherValue => {
          this.voucherError$.next(false);
          this.subSink.sink = this.basket$.pipe(take(1)).subscribe(
            (basket: Basket) => {
              let canAddPromotion = true;
              if (basket.appliedVouchers[0]) {
                if (basket.appliedVouchers[0].code === voucherValue) {
                  canAddPromotion = false;
                }
              }
              if (canAddPromotion) {
                if (voucherValue === '') {
                  this.subSink.sink = this.observer.subscribeToAction(
                    this.basketsFacade.deletePromotion(basket.id, basket.appliedVouchers[0].code),
                    () => {
                      this.toastService.show(
                        {
                          type: 'success',
                          text: this.translateService.instant('toast.promotion.delete.success')
                        }
                      );
                    },
                    () => {
                      this.voucherError$.next(true);
                      this.toastService.show(
                        {
                          type: 'error',
                          text: this.translateService.instant('toast.promotion.delete.error')
                        }
                      );
                    }
                  );
                  return;
                }
                this.subSink.sink = this.observer.subscribeToAction(
                  this.basketsFacade.addPromotion(basket.id, voucherValue),
                  () => {
                    this.toastService.show(
                      {
                        type: 'success',
                        text: this.translateService.instant('toast.promotion.add.success')
                      }
                    );
                  },
                  () => {
                    this.voucherError$.next(true);
                    this.toastService.show(
                      {
                        type: 'error',
                        text: this.translateService.instant('toast.promotion.add.error')
                      }
                    );
                  }
                );
              }
            }
          )
        }
      );
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }


  quantityChanged(quantity: { entryNumber: number, quantity: number }) {
    this.subSink.sink = this.basket$.pipe(take(1)).subscribe(
      (basket: Basket) => {
        this.subSink.sink = this.observer.subscribeToAction(
          this.basketsFacade.changeQuantity(basket.id, quantity.entryNumber.toString(), quantity.quantity),
          () => {
            this.toastService.show(
              {
                type: 'success',
                text: this.translateService.instant('toast.quantity.update.success')
              }
            );
          },
          () => {
            this.toastService.show(
              {
                type: 'error',
                text: this.translateService.instant('toast.quantity.update.delete')
              }
            );
          }
        )
      }
    )
  }

  deleteEntry(entryNumber: number) {
    this.subSink.sink = this.basket$.pipe(take(1)).subscribe(
      (basket: Basket) => {
        this.subSink.sink = this.observer.subscribeToAction(
          this.basketsFacade.deleteEntry(basket.id, entryNumber.toString()),
          () => {
            this.toastService.show(
              {
                type: 'success',
                text: this.translateService.instant('toast.basket.delete.success')
              }
            );
          },
          () => {
            this.toastService.show(
              {
                type: 'error',
                text: this.translateService.instant('toast.basket.delete.error')
              }
            );
          }
        )
      }
    )
  }
}
